<template>
  <FlashNotifications ref="flash" />
  <Teleport to="body">
    <Transition name="modal" appear>
      <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeModal"></div>
        <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 w-full max-w-lg animate-in max-h-[90vh] flex flex-col">

          <!-- Header -->
          <div class="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                <svg class="w-4.5 h-4.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z"/>
                </svg>
              </div>
              <div>
                <h3 class="text-base font-bold text-gray-900 dark:text-white leading-tight">{{ I18N.features.organization.invite_members }}</h3>
                <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">They'll get an email invite to join your organization.</p>
              </div>
            </div>
            <button @click="closeModal" type="button"
              class="w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="px-6 py-5 overflow-y-auto space-y-5">
            <!-- Email addresses -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{{ I18N.features.organization.email_addresses }}</label>
                <div class="flex items-center gap-3">
                  <span v-if="validEmailCount > 0" class="text-[11px] font-medium text-blue-600 dark:text-blue-400 tabular-nums">
                    {{ validEmailCount }} {{ validEmailCount === 1 ? 'person' : 'people' }} ready
                  </span>
                  <button v-if="hasAnyEmailInput" type="button" @click="clearAllEmails"
                    class="text-[11px] font-medium text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                    Clear all
                  </button>
                </div>
              </div>

              <!-- Multi-email tag input -->
              <div @click="focusInput"
                class="group relative overflow-y-auto max-h-[32vh] flex flex-wrap content-start gap-1.5 cursor-text border rounded-xl w-full min-h-[112px] p-3 bg-white dark:bg-[#0d1117] transition-all"
                :class="hasInvalidEmail ? 'border-red-300 dark:border-red-800' : 'border-gray-200 dark:border-gray-700 focus-within:border-blue-500'">
                <div class="pointer-events-none absolute inset-0 rounded-xl transition-shadow"
                  :class="hasInvalidEmail ? 'ring-2 ring-red-500/10' : 'group-focus-within:ring-2 group-focus-within:ring-blue-500/20'"></div>

                <span v-for="(email, index) in guestEmails" :key="index"
                  class="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 text-xs font-medium rounded-lg ring-1 ring-inset"
                  :class="isValidEmail(email)
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 ring-blue-100 dark:ring-blue-800/60'
                    : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 ring-red-100 dark:ring-red-800/60'"
                  :title="isValidEmail(email) ? '' : 'Doesn\'t look like a valid email address'">
                  <svg v-if="!isValidEmail(email)" class="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.28 11.18c.75 1.334-.213 2.987-1.742 2.987H3.72c-1.53 0-2.493-1.653-1.743-2.987l6.28-11.18ZM11 13a1 1 0 11-2 0 1 1 0 012 0Zm-.25-6.75a.75.75 0 00-1.5 0v3.5a.75.75 0 001.5 0v-3.5Z" clip-rule="evenodd"/>
                  </svg>
                  <span class="break-all">{{ email }}</span>
                  <button type="button" @click="removeGuest(index)" class="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10 transition-colors">
                    <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="m16.192 6.344-4.243 4.242-4.242-4.242-1.414 1.414L10.535 12l-4.242 4.242 1.414 1.414 4.242-4.242 4.243 4.242 1.414-1.414L13.364 12l4.242-4.242z"/>
                    </svg>
                  </button>
                </span>

                <input ref="textInput" type="text" v-model="newEmail"
                  @paste="addGuestFromPaste" @focusout="addGuest" @keydown="handleCommitKey"
                  :placeholder="guestEmails.length === 0 ? 'Type or paste email addresses…' : 'Add another…'"
                  class="relative flex-grow min-w-[160px] text-sm bg-transparent border-0 py-1 text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-0" />
              </div>

              <p v-if="hasInvalidEmail" class="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                <svg class="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.28 11.18c.75 1.334-.213 2.987-1.742 2.987H3.72c-1.53 0-2.493-1.653-1.743-2.987l6.28-11.18ZM11 13a1 1 0 11-2 0 1 1 0 012 0Zm-.25-6.75a.75.75 0 00-1.5 0v3.5a.75.75 0 001.5 0v-3.5Z" clip-rule="evenodd"/>
                </svg>
                One or more email addresses look invalid. Fix or remove them before sending.
              </p>
              <p v-else class="mt-2 text-xs text-gray-400 dark:text-gray-500">
                Separate addresses with a comma, space, or new line — or just paste a whole list.
              </p>
            </div>

            <!-- Role -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{{ I18N.features.organization.role }}</label>
              <div class="grid grid-cols-2 gap-2">
                <button type="button" @click="role = 'member'"
                  class="rounded-lg text-left py-2.5 px-3 border transition-colors"
                  :class="role === 'member' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'">
                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.features.organization.member }}</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ I18N.features.organization.member_subheading }}</p>
                </button>
                <button type="button" @click="role = 'admin'"
                  class="rounded-lg text-left py-2.5 px-3 border transition-colors"
                  :class="role === 'admin' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'">
                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ I18N.features.organization.admin }}</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ I18N.features.organization.admin_subheading }}</p>
                </button>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-6 py-4 bg-gray-50 dark:bg-[#0d1117] border-t border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3 flex-shrink-0">
            <p class="text-xs text-gray-400 dark:text-gray-500">
              <template v-if="validEmailCount > 0">Inviting {{ validEmailCount }} {{ validEmailCount === 1 ? 'person' : 'people' }} as {{ role === 'admin' ? I18N.features.organization.admin : I18N.features.organization.member }}</template>
            </p>
            <div class="flex items-center gap-3 flex-shrink-0">
              <button @click="closeModal" type="button" :disabled="sending"
                class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-60">
                {{ I18N.application.bookings.upcoming.cancel }}
              </button>
              <button type="button" @click="sendInvite" :disabled="!canSend || sending"
                class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-900/40 disabled:cursor-not-allowed rounded-lg transition-colors">
                <svg v-if="sending" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                {{ I18N.features.organization.send_invitations }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { orgStore } from 'controllers/vue_components/organizations/store'
import { useRequest } from 'composables/useRequest.js'
import { useConstants } from 'composables/useConstants.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'

const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const { I18N } = useConstants()
const store = orgStore()

const guestEmails = ref([])
const newEmail = ref('')
const role = ref('member')
const sending = ref(false)
const textInput = ref(null)

const hasInvalidEmail = computed(() => guestEmails.value.some((email) => !isValidEmail(email)))

const validEmailCount = computed(() => guestEmails.value.filter((email) => isValidEmail(email)).length)

const hasAnyEmailInput = computed(() => guestEmails.value.length > 0 || newEmail.value.trim() !== '')

const canSend = computed(() => !hasInvalidEmail.value && guestEmails.value.length > 0)

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function addGuestFromPaste(event) {
  event.preventDefault()
  newEmail.value = event.clipboardData.getData('text')
  addGuest()
}

function addGuest() {
  const emails = newEmail.value.trim().split(/[\s,]+/).map((e) => e.trim())
  emails.forEach((email) => { if (email !== '') guestEmails.value.push(email) })
  newEmail.value = ''
}

function removeGuest(index) {
  guestEmails.value.splice(index, 1)
}

function clearAllEmails() {
  guestEmails.value = []
  newEmail.value = ''
  focusInput()
}

function focusInput() {
  textInput.value?.focus()
}

function handleCommitKey(event) {
  if (newEmail.value.trim() === '') return
  if ([' ', 'Enter', ','].includes(event.key)) {
    event.preventDefault()
    addGuest()
  }
}

function resetForm() {
  guestEmails.value = []
  newEmail.value = ''
  role.value = 'member'
}

function closeModal() {
  store.showInviteModal = false
  resetForm()
}

async function sendInvite() {
  sending.value = true
  const res = await axiosRequest('POST', `/api/v1/organizations/${store.current_org.id}/members`,
    { members: { user_emails: guestEmails.value, role: role.value } }, false)
  sending.value = false

  if (res.js_error === false && res.response.success) {
    flash.value?.show(res.response.message, 'notice')
    await store.fetchOrgMembers()
    closeModal()
  } else {
    flash.value?.show(failureMessage(res.response), 'alert')
  }
}

function failureMessage(response) {
  const perEmailErrors = response?.data
  if (Array.isArray(perEmailErrors) && perEmailErrors.length > 0) {
    return perEmailErrors
      .map((entry) => `${entry.member_params?.user_email}: ${entry.errors?.join(', ')}`)
      .join(' | ')
  }
  return response?.message || 'Something went wrong'
}

function handleDelete(event) {
  if ((event.key === 'Delete' || event.key === 'Backspace') && newEmail.value === '' && document.activeElement === textInput.value) {
    removeGuest(guestEmails.value.length - 1)
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleDelete)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleDelete)
})
</script>
